import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDefaultPageSize, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCurrentPromotionsDataView,
    getPromotionsDataView,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import CartLinesList, { CartLinesListStyles } from "@insite/content-library/Components/CartLinesList";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Link from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";
import React, { ChangeEvent, FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {  
    orderNotesData: any;
    notesErrorData: any;
}
const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const promotionsDataView = cartId ? getPromotionsDataView(state, cartId) : getCurrentPromotionsDataView(state);

    return {
        cartState,
        promotionsDataView,
        defaultPageSize: getDefaultPageSize(state),
        settingsCollection: getSettingsCollection(state),
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
    };
};

const mapDispatchToProps = {};

type Props =  OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutReviewAndSubmitProductListStyles {
    centeringWrapper?: InjectableCss;
    orderLinesList?: CartLinesListStyles;
    textArea?: TextAreaProps;
    notesWrapper?: InjectableCss;
    notesHeading?: InjectableCss;
    iconStyle?: InjectableCss;
    notesArea?: InjectableCss;
    parentContainer?: InjectableCss;
    productsCannotBePurchasedAllModal?: ModalPresentationProps;
    productsCannotBePurchasedContainer?: InjectableCss;
    productsCannotBePurchasedMessage?: InjectableCss;    
    confirmMessage?: InjectableCss;
    closeContainer?: InjectableCss;
    confirmButtons?: InjectableCss;
    removeButton?: ButtonPresentationProps;
}

export const productListStyles: CheckoutReviewAndSubmitProductListStyles = {
    parentContainer: {
        css: css`
            h2.section-heading {
                &:before {
                    display: inline-block;
                    border: 0.0625rem solid ${themeColors.colorPrimary};
                    border-radius: 100%;
                    content: attr(data-number);
                    height: 1.5625rem;
                    width: 1.5625rem;
                    background-color: ${themeColors.colorPrimary};
                    color: ${themeColors.colorWhite};
                    font-family: 'Roboto', sans-serif;
                    font-size: ${themeFontSizes.size16};
                    font-weight: ${themeFontWeights.fontWeightForBold};
                    letter-spacing: 0;
                    line-height: 1.5rem;
                    margin-right: .9375rem;
                    text-align: center;
                    vertical-align: middle;
                }
            }
            .quantity-container {
                p[data-test-selector="heading"] {
                    font-family:'Roboto', sans-serif;
                    font-size: .875rem;
                }
                p[data-test-selector="text"] {
                    font-family:'Roboto', sans-serif;
                    font-size: .875rem;
                }
            }

            @media screen and (max-width: 767px) {
                .each-price-container {
                    display: none !important;
                }
                .quantity-container {
                    p[data-test-selector="heading"] {
                        font-size:.875rem;
                        min-width: fit-content !important;
                        margin-right:1.55rem;                        
                        letter-spacing:0 !important;
                        &:after {
                            content: none !important;
                        }
                    }
                    p[data-test-selector="text"] {
                        font-size:.875rem;
                    }
                }
                .price-container {
                    p[data-test-selector="heading"] {
                        min-width: 5.5rem !important;
                        width: 100%;
                    }

                    p[data-test-selector="text"] {
                        opacity: 0.7;
                        color: ${themeColors.colorDanger};
                        font-family: 'Roboto', sans-serif;
                        font-size: ${themeFontSizes.size20};
                        font-weight: ${themeFontWeights.fontWeightForBold};
                        letter-spacing: -0.0238rem;
                        line-height: 1.4375rem;
                    }

                    > div {
                        flex-direction: column;
                    }
                }
                
            }
            
        `,
    },
    orderLinesList: {
        header: {
            wrapper:{
                css: css`
                display: flex;
                justify-content: space-between;
                border-top: 0.0625rem solid ${themeColors.colorBorderBrands};
                border-bottom: 0.0625rem solid ${themeColors.colorBorderBrands};
                padding-top: 1.25rem;
                padding-bottom: 1.25rem;

                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                null,
                                css`
                                    border-top: none;
                                    padding-top: 2rem;
                                    padding-bottom: 2rem;
                                `,
                            ],
                            "max",
                        )}        
                `,
            }
        }    
    },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 18.75rem;
        `,
    },
    textArea: {
        css: css`
            min-width: 100%;
            max-width: 100%;
            min-height: 3.125rem;
        `,
    },
    notesWrapper: {
        css: css`
            border-radius: 0.125rem;
            background-color: ${themeColors.colorNotes};
            border: 0.0625rem solid ${themeColors.colorNotes};
            max-width: 41.25rem;
            padding: 0.625rem 1.5625rem 0.625rem 1.5625rem;
            margin: 0.9375rem 0 1.5625rem 0;
            label[data-test-selector="notes-msg-label"]{
                display: none;
            }

            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    null,
                    css`
                        margin: 0.9375rem 0 1.5625rem 2.5rem;
                    `,
                ],
                "min",
            )}

        `,
    },
    notesHeading: {
        css: css`
            color: ${themeColors.colorPrimary};
            font-family: 'Roboto', sans-serif;
            font-size: ${themeFontSizes.size16};
            font-weight: ${themeFontWeights.fontWeightMedium};
            letter-spacing: 0.0112rem;
            line-height: 1.4375rem;
            cursor: pointer;
            span {
                display: inline-block;
                vertical-align: middle;
            }
            .order-label {
                color: ${themeColors.colorPrimary};
                font-family: 'Roboto', sans-serif;
                font-size: ${themeFontSizes.size16};
                font-weight: ${themeFontWeights.fontWeightMedium};
                letter-spacing: 0.0112rem;
            }
        `,
    },
    iconStyle: {
        css: css`
            display: inline-block;
            vertical-align: middle;
            margin-right: 0.3125rem;
            height: 1.125rem;
            width: 1.125rem;
            svg {
                width: 1.2rem;
                height: 1.2rem;
                scale: 0.8;
                color: ${themeColors.colorEndeavour};
            }
        `,
    },
    notesArea: {
        css: css`
            display: none;

            &.active {
                display: block;
            }
        `,
    },
    productsCannotBePurchasedAllModal: {
        sizeVariant: "small",
    },
    closeContainer: {
        css: css`
           position: absolute;
           right: 1.25rem;
           top: 24px;
           .close-label {
                color: #535E64;
                font-family: Roboto;
                font-size: .875rem;
                letter-spacing: 0.16px;
                line-height: 1;
                display: inline-block;
                margin-right: .625rem;
                vertical-align: middle;
           }
           .icon-close {
                display: inline-block;
                cursor: pointer;
                vertical-align: middle;
                line-height: 1;
                width: 1.125rem;
                height: 1.125rem;
                svg {
                    height: 1.125rem;
                    width: 1.125rem;
                }
            }
        `,
    },
    confirmMessage: {
        css: css`
            h1 {
                color: #002554;
                font-family: 'Roboto', sans-serif;
                font-size: 1.25rem;
                font-weight: 500;
                letter-spacing: 0;
                line-height: 32px;
                position: relative;
                margin-bottom: 2.1875rem;
                margin-top: .5rem;

                &:after {
                    height: 2px;
                    width: 44px;
                    background-color: #C63527;
                    content: '';
                    display: inline-block;
                    position: absolute;
                    bottom: -12px;
                    left: 1px;
                }
            }

            .icon-alert {
                svg {
                    transform: scale(1.5);
                    stroke: #c63527;
                }
            }

            p {
                color: #002554;
                font-family: Lato, sans-serif;
                font-size: .9375rem;
                letter-spacing: 0;
                line-height: 1.3125rem;
            }
        `,
    },
    removeButton: {
        buttonType: "solid",
        css: css`
            border-radius: 2px;
            background-color: #002554;
            border: 2px solid #002554;
            color: #fff;
            text-transform: uppercase;
            float: right;
            &:hover,
            &:active {
                background-color: #002554;
                border: 2px solid #002554
            }
            span {
                color: #fff;
            }
        `,
    },
    confirmButtons: {
        css: css`         
            padding-top: 1.625rem;
            margin-top: 1.875rem;
            border-top: 1px solid #D7D7D7;
        `,
    },
    
};

const styles = productListStyles;

const CheckoutReviewAndSubmitProductList: React.FC<Props> = ({ cartState, promotionsDataView, settingsCollection,orderNotesData,cartPageUrl, notesErrorData }) => {
    const [isCondensed, setIsCondensed] = useState(false);
    const [notes, setOrderNote] = useState("");
    const [showText, setShowText] = React.useState(false);
    const [isProductsCannotBePurchased, setProductsCannotBePurchased] = React.useState(false);
    const [notesError, setNotesError] = React.useState("");

    useEffect(() => {
        if(cartState.value && cartState.value.cartLines) {
            setNotesError(notesErrorData);
        }
    },[notesErrorData]);

    if (!cartState.value || !cartState.value.cartLines || !promotionsDataView.value) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }
    
    const notesChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setOrderNote(event.currentTarget.value);
        orderNotesData(event.currentTarget.value);
    };

    const handleTooltipKeyDown = (e: React.KeyboardEvent) => {
        if (e.keyCode === 27) {
            e.stopPropagation();
            setShowText(false);
        }
        if (e.keyCode === 13) {
            e.stopPropagation();
            setShowText(!showText);
        }
    };

    const { productSettings } = settingsCollection;
    const cartLines =cartState.value.status === "Cart" ? cartState.value.cartLines.filter(cartline => !cartline.quoteRequired) : cartState.value.cartLines;
    const productsCannotBePurchased = cartLines.some(o => o.isRestricted || !o.isActive);

    if(isProductsCannotBePurchased!==productsCannotBePurchased)
    {
        setProductsCannotBePurchased(productsCannotBePurchased);     
        if(productsCannotBePurchased)
        {
            setTimeout(() => confirmProductsCannotBePurchased(), 5000);
        }   
    }     
    const confirmProductsCannotBePurchased = () => {
        setProductsCannotBePurchased(false);
        if (cartPageUrl) {
            window.location.href = cartPageUrl;
        }
    };
    return (
        <>
            <StyledWrapper {...styles.parentContainer}>
                <h2 className="section-heading">{translate("Review your Products")}</h2>
                <Typography as="div" {...styles.notesWrapper}>
                    <Link as="a" {...styles.notesHeading}
                        onClick={() => setShowText(!showText)}
                        onKeyDown={(e) => handleTooltipKeyDown(e)}
                        tabIndex={0} role="button"
                    >   
                        {!showText ?
                            <Icon {...styles.iconStyle} src={ChevronRight} /> :
                            <Icon {...styles.iconStyle} src={ChevronDown} /> 
                        }
                        <Typography className="order-label"> {translate("Order Notes")}</Typography>
                    </Link>
                    <Typography {...styles.notesArea} as="div" className={`${showText ? 'active' : ''}`}>
                        <TextArea
                            {...styles.textArea}
                            label={translate("Add Order Notes")}
                            value={notes}
                            error={notesError}
                            onChange={notesChangeHandler}
                            data-test-selector="notes-msg"
                        />
                    </Typography>
                </Typography>
                <CartLinesList
                    cart={cartState.value}
                    promotions={promotionsDataView.value}
                    isCondensed={isCondensed}
                    onChangeIsCondensed={(event: React.SyntheticEvent<Element, Event>, value: boolean) => setIsCondensed(value)}
                    editable={false}
                    showSavingsAmount={productSettings.showSavingsAmount}
                    showSavingsPercent={productSettings.showSavingsPercent}
                    extendedStyles={styles.orderLinesList}
                />
            </StyledWrapper>
        
            <Modal
            isOpen={isProductsCannotBePurchased}
            data-test-selector="productsCannotBePurchased"
            {...styles.productsCannotBePurchasedAllModal}
            handleClose={() => confirmProductsCannotBePurchased()}
            className="confirm-remove-modal"
        >
            <Typography as="div" {...styles.productsCannotBePurchasedContainer}>                
                <Typography as="div" {...styles.confirmMessage}>
                    <Icon className="icon-alert" src="AlertCircle" />
                    <h1>{translate("Product Restriction")}</h1>
                    <p>{translate("One or more products in the cart is restricted. You will be redirected back to the cart.")}</p>
                </Typography>
                <Typography as="div" {...styles.confirmButtons}>                    
                    <Button onClick={confirmProductsCannotBePurchased} {...styles.removeButton}>{translate("Ok")}</Button>
                </Typography>
            </Typography>
        </Modal>
        </>
        
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(CheckoutReviewAndSubmitProductList);
